import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Fab,
} from "@mui/material";
import DynamicTable from "../../../../data/DynamicTable/dynamicTable"; 
import { documentManagementUrl } from "../../../../config";
import DocumentUploadForm from "./Form";
import { DocumentUploadColumns } from "../../../../data/DynamicTable/Collumns";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../../../theme";
import { useTheme } from "@mui/material";
import { UpdateDataToStore } from "../../../../store/Actions/CustomAction";

const base_url = documentManagementUrl.uri;

const actions = {
  entity: "Document Uploads",
  isSubRight: true,
  add: {
    key: "ADD",
    button_name: "Add",
    Show_Button: true,
    permission: "CreatePermission",
  },
  edit: {
    key: "EDIT",
    button_name: "Edit A",
    Show_Button: false,
    permission: "CreatePermission",
  },
  viewDocument: {
    key: "VIEW_DOCUMENT",
    button_name: "Open Document",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  documentAccess: {
    key: "VIEW_DOCUMENT",
    button_name: "Manage Document Permissions",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  deleteDocument: {
    key: "DELETE_DOCUMENT",
    button_name: "Delete Document",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  dataObject: {
    inputObjectName: "DocumentsByUserAndCase",
    inputSubObjectName: "documentsByCase",
    returnFields: [
      "id",
      "caseTitle",
      "title",
      "caseNumber",
      "fileName",
      "firstName",
      "lastName",
      "roleName",
      "authorId",
      "caseId",
      "createdDate",
      "lastModifiedDate",
      "filePath",
      "companyId",
      "deleted",
      "deletedDate",
    ],
    isDocumentUplod: true,
  },
  showStatus: true,
};

function DocumentUploads() {
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const caseId = location.state?.caseId || JSON.parse(localStorage.getItem("CaseId"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleGoBack = () => {
    const backPath = location.state?.from || "/Cases";
    navigate(backPath);
  };

  const storeData = useSelector(
    (state) => state.custom.customData.caseManagementModel.documentUploads
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileName, setFileName] = useState("");
  const [contentType, setContentType] = useState("");
  const [wordHtml, setWordHtml] = useState(null);
  const [excelHtml, setExcelHtml] = useState(null);

  const getFileExtension = (url) => {
    if (!url) return null;
    const urlWithoutQuery = url.split("?")[0];
    return urlWithoutQuery.split(".").pop().toLowerCase();
  };

  const getViewerType = (extension, contentType = "") => {
    const type = contentType?.toLowerCase() || "";
    const ext = extension?.toLowerCase() || "";

    if (type.includes("pdf")) return "pdf";
    if (type.includes("word") || type.includes("msword")) return "word";
    if (type.includes("excel") || type.includes("spreadsheet")) return "excel";
    if (type.includes("powerpoint") || type.includes("presentation")) return "powerpoint";
    if (type.includes("image")) return "image";
    if (type.includes("text")) return "text";

    if (["pdf"].includes(ext)) return "pdf";
    if (["doc", "docx"].includes(ext)) return "word";
    if (["xls", "xlsx"].includes(ext)) return "excel";
    if (["ppt", "pptx"].includes(ext)) return "powerpoint";
    if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(ext)) return "image";
    if (["txt", "csv", "rtf"].includes(ext)) return "text";
    
    return "unknown";
  };

  const isOfficeDocument = (viewerType) => {
    return ["word", "excel", "powerpoint"].includes(viewerType);
  };

  // Load mammoth.js for Word documents
  const loadMammoth = () => {
    return new Promise((resolve, reject) => {
      if (window.mammoth) {
        resolve(window.mammoth);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js';
      script.onload = () => resolve(window.mammoth);
      script.onerror = () => reject(new Error('Failed to load mammoth.js'));
      document.head.appendChild(script);
    });
  };

  // Load SheetJS (xlsx) for Excel documents
  const loadXLSX = () => {
    return new Promise((resolve, reject) => {
      if (window.XLSX) {
        resolve(window.XLSX);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      script.onload = () => resolve(window.XLSX);
      script.onerror = () => reject(new Error('Failed to load SheetJS'));
      document.head.appendChild(script);
    });
  };

  const convertWordToHtml = async (arrayBuffer) => {
    try {
      const mammoth = await loadMammoth();
      const result = await mammoth.convertToHtml({ 
        arrayBuffer,
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          "p[style-name='Title'] => h1.title:fresh",
          "r[style-name='Strong'] => strong",
          "r[style-name='Emphasis'] => em",
        ],
        convertImage: mammoth.images.imgElement(function(image) {
          return image.read("base64").then(function(imageBuffer) {
            return {
              src: "data:" + image.contentType + ";base64," + imageBuffer
            };
          });
        })
      });
      
      let html = result.value;
      
      // Split content into pages based on page breaks or content length
      // Word documents use section breaks and page breaks
      const pageBreakMarkers = [
        '<br style="page-break-before:always">',
        '<p style="page-break-before: always">',
        '</section>',
        '<!-- pagebreak -->'
      ];
      
      // First, try to split by actual page break markers
      let pages = [html];
      pageBreakMarkers.forEach(marker => {
        if (html.includes(marker)) {
          pages = html.split(marker);
        }
      });
      
      // If no explicit breaks found, estimate pages by content length
      // Average Word page has ~3000 characters
      if (pages.length === 1 && html.length > 3000) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const elements = Array.from(tempDiv.children);
        
        pages = [];
        let currentPage = '';
        let currentLength = 0;
        const avgPageLength = 3000;
        
        elements.forEach((element, index) => {
          const elementHtml = element.outerHTML;
          const elementLength = element.textContent.length;
          
          // Start new page if current page is getting too long
          // But keep tables together on same page
          if (currentLength > avgPageLength && element.tagName !== 'TABLE' && currentPage !== '') {
            pages.push(currentPage);
            currentPage = elementHtml;
            currentLength = elementLength;
          } else {
            currentPage += elementHtml;
            currentLength += elementLength;
          }
          
          // Push last page
          if (index === elements.length - 1 && currentPage) {
            pages.push(currentPage);
          }
        });
      }
      
      // Style each page
      const styledPages = pages.map((pageContent, index) => {
        let styledContent = pageContent;
        
        // Style tables
        styledContent = styledContent.replace(/<table>/g, '<table style="border-collapse: collapse; width: 100%; margin: 20px 0; border: 1px solid #ddd; background: white;">');
        styledContent = styledContent.replace(/<table /g, '<table style="border-collapse: collapse; width: 100%; margin: 20px 0; border: 1px solid #ddd; background: white;" ');
        
        // Style table cells
        styledContent = styledContent.replace(/<td>/g, '<td style="border: 1px solid #ddd; padding: 10px; color: #000; vertical-align: top;">');
        styledContent = styledContent.replace(/<td /g, '<td style="border: 1px solid #ddd; padding: 10px; color: #000; vertical-align: top;" ');
        
        // Style table headers
        styledContent = styledContent.replace(/<th>/g, '<th style="border: 1px solid #ddd; padding: 10px; background: #f5f5f5; font-weight: bold; color: #000; text-align: left;">');
        styledContent = styledContent.replace(/<th /g, '<th style="border: 1px solid #ddd; padding: 10px; background: #f5f5f5; font-weight: bold; color: #000; text-align: left;" ');
        
        // Style table rows
        styledContent = styledContent.replace(/<tr>/g, '<tr style="background: white;">');
        styledContent = styledContent.replace(/<tr /g, '<tr style="background: white;" ');
        
        // Style paragraphs
        styledContent = styledContent.replace(/<p>/g, '<p style="margin: 10px 0; color: #000; line-height: 1.6;">');
        styledContent = styledContent.replace(/<p /g, '<p style="margin: 10px 0; color: #000; line-height: 1.6;" ');
        
        // Style lists
        styledContent = styledContent.replace(/<ul>/g, '<ul style="margin: 10px 0; padding-left: 30px; color: #000;">');
        styledContent = styledContent.replace(/<ol>/g, '<ol style="margin: 10px 0; padding-left: 30px; color: #000;">');
        styledContent = styledContent.replace(/<li>/g, '<li style="margin: 5px 0; color: #000;">');
        
        // Style headings
        styledContent = styledContent.replace(/<h1>/g, '<h1 style="color: #000; margin: 20px 0 10px 0; font-size: 24px; font-weight: bold;">');
        styledContent = styledContent.replace(/<h2>/g, '<h2 style="color: #000; margin: 18px 0 10px 0; font-size: 20px; font-weight: bold;">');
        styledContent = styledContent.replace(/<h3>/g, '<h3 style="color: #000; margin: 16px 0 10px 0; font-size: 18px; font-weight: bold;">');
        
        // Wrap each page in a page container with page number
        return `
          <div style="
            background: white;
            padding: 60px 80px;
            margin: 20px auto;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            min-height: 800px;
            max-width: 850px;
            position: relative;
            page-break-after: always;
          ">
            <div style="
              position: absolute;
              bottom: 30px;
              right: 80px;
              color: #999;
              font-size: 12px;
              font-family: Arial, sans-serif;
            ">Page ${index + 1}</div>
            ${styledContent}
          </div>
        `;
      });
      
      return styledPages.join('\n');
    } catch (err) {
      console.error('Word conversion error:', err);
      throw new Error('Failed to convert Word document to HTML');
    }
  };

  const convertExcelToHtml = async (arrayBuffer) => {
    try {
      const XLSX = await loadXLSX();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      let html = '<div style="font-family: Arial, sans-serif;">';
      
      workbook.SheetNames.forEach((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const htmlTable = XLSX.utils.sheet_to_html(worksheet, {
          id: `sheet-${index}`,
          editable: false
        });
        
        html += `
          <div style="margin-bottom: 30px;">
            <h2 style="background: #4CAF50; color: white; padding: 10px; margin: 0;">
              ${sheetName}
            </h2>
            <div style="overflow-x: auto; border: 1px solid #ddd; background: white;">
              ${htmlTable}
            </div>
          </div>
        `;
      });
      
      html += '</div>';
      
      // Add comprehensive styling to the tables with proper text visibility
      html = html.replace(/<table/g, '<table style="border-collapse: collapse; width: 100%; font-size: 13px; background: white;"');
      html = html.replace(/<td/g, '<td style="border: 1px solid #ddd; padding: 8px; color: #000; background: white; min-width: 80px;"');
      html = html.replace(/<th/g, '<th style="border: 1px solid #ddd; padding: 8px; background: #f2f2f2; font-weight: bold; color: #000; text-align: left;"');
      html = html.replace(/<tr/g, '<tr style="background: white;"');
      
      return html;
    } catch (err) {
      console.error('Excel conversion error:', err);
      throw new Error('Failed to convert Excel document to HTML');
    }
  };

  const convertPowerPointToHtml = async (arrayBuffer) => {
    try {
      // For PowerPoint, we'll create a simple preview showing it's available for download
      // Full PPTX rendering would require a much larger library
      return `
        <div style="font-family: Arial, sans-serif; padding: 40px; text-align: center;">
          <div style="max-width: 600px; margin: 0 auto; background: #f5f5f5; padding: 40px; border-radius: 8px;">
            <svg width="120" height="120" viewBox="0 0 24 24" style="margin-bottom: 20px;">
              <path fill="#D04423" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18.5,20H5.5V4H13V9H18.5V20M7,11H9V18H7V11M10,11H12V18H10V11M13,11H15V18H13V11M16,11H18V18H16V11Z"/>
            </svg>
            <h2 style="color: #D04423; margin-bottom: 10px;">PowerPoint Presentation</h2>
            <p style="color: #666; margin-bottom: 20px;">
              PowerPoint presentations are ready to download. Full slide preview is not available in the browser.
            </p>
            <p style="color: #999; font-size: 14px;">
              Download the file to view it in Microsoft PowerPoint, Google Slides, or compatible applications.
            </p>
          </div>
        </div>
      `;
    } catch (err) {
      console.error('PowerPoint conversion error:', err);
      throw new Error('Failed to process PowerPoint document');
    }
  };

  const fetchDocument = async (url) => {
    setLoading(true);
    setError(null);
    setWordHtml(null);
    setExcelHtml(null);

    try {
      const token = localStorage.getItem("token");

      if (!token || token.trim() === "") {
        throw new Error("Authentication token missing. Please log in.");
      }

      const formattedToken = `Bearer ${token.replace(/"/g, "")}`;
      const FILE_GATEWAY_URL = "https://matrix.mcb.co.ke";


      const response = await fetch(url, {
  method: "GET",
  headers: url.includes(FILE_GATEWAY_URL) || url.includes("X-Amz-Signature")
    ? {}
    : { Authorization: formattedToken },
});

      if (response.status === 401) {
        throw new Error("Unauthorized access. Please refresh the page or log in again.");
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch document (Status: ${response.status})`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get("content-type");
      setContentType(contentType);

      const blobObjectUrl = URL.createObjectURL(blob);
      setBlobUrl(blobObjectUrl);

      const extension = getFileExtension(url);
      const detectedType = getViewerType(extension, contentType);
      setFileType(detectedType);

      // Convert Office documents to HTML for better viewing
      if (detectedType === "word") {
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const html = await convertWordToHtml(arrayBuffer);
          setWordHtml(html);
        } catch (conversionError) {
          console.error("Word conversion failed:", conversionError);
        }
      } else if (detectedType === "excel") {
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const html = await convertExcelToHtml(arrayBuffer);
          setExcelHtml(html);
        } catch (conversionError) {
          console.error("Excel conversion failed:", conversionError);
        }
      } else if (detectedType === "powerpoint") {
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const html = await convertPowerPointToHtml(arrayBuffer);
          setWordHtml(html); // Reuse wordHtml state for PowerPoint preview
        } catch (conversionError) {
          console.error("PowerPoint conversion failed:", conversionError);
        }
      }

    } catch (err) {
      setError(err.message);
      console.error("Error fetching document:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeData.currentDocument) {
      const docUrl = storeData.currentDocument;
      setCurrentDoc(docUrl);
      setOpenDialog(true);

      try {
        const url = new URL(docUrl);
        const params = new URLSearchParams(url.search);
        const fileParam = params.get("file");

        if (fileParam) {
          const cleanName = decodeURIComponent(fileParam)
            .split("/")
            .pop()
            .split("?")[0]
            .trim();
          setFileName(cleanName.replace(/^.*?_/, "").trim());
        } else {
          const parts = docUrl.split("/");
          setFileName(parts[parts.length - 1].split("?")[0]);
        }
      } catch {
        const parts = docUrl.split("/");
        setFileName(parts[parts.length - 1].split("?")[0]);
      }

      fetchDocument(docUrl);
    }
  }, [storeData.currentDocument]);

  const handleClose = () => {
    setOpenDialog(false);
    dispatch(UpdateDataToStore("UPDATE_ACTIVE_DOCUMENT", null));
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setCurrentDoc(null);
    setError(null);
    setFileType(null);
    setWordHtml(null);
    setExcelHtml(null);
  };

  const handleOpenInNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank");
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    const extension = getFileExtension(currentDoc) || fileType;
    link.download = fileName || `document.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderDocumentViewer = () => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
          <CircularProgress />
          <Typography>Loading document.</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
          <Typography color="error" variant="h6">Document Load Error:</Typography>
          <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchDocument(currentDoc)}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (!currentDoc || !blobUrl) return null;

    // Handle Word documents
    if (fileType === "word") {
      if (wordHtml) {
        return (
          <Box sx={{ 
            width: "100%", 
            height: "100%", 
            overflow: "auto", 
            bgcolor: "#e5e5e5",
            p: 2
          }}>
            <div 
              dangerouslySetInnerHTML={{ __html: wordHtml }}
              style={{
                fontFamily: 'Calibri, Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#000'
              }}
            />
          </Box>
        );
      } else {
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
            <Typography variant="h6">Word Document Ready</Typography>
            <Typography variant="body2" color="textSecondary">{fileName}</Typography>
            <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
              Preview conversion failed. Please download the document to view it.
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download Document
            </Button>
          </Box>
        );
      }
    }

    // Handle Excel documents
    if (fileType === "excel") {
      if (excelHtml) {
        return (
          <Box sx={{ 
            width: "100%", 
            height: "100%", 
            overflow: "auto", 
            p: 3, 
            bgcolor: "#ffffff"
          }}>
            <div 
              dangerouslySetInnerHTML={{ __html: excelHtml }}
              style={{
                color: '#000000',
                backgroundColor: '#ffffff'
              }}
            />
          </Box>
        );
      } else {
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
            <Typography variant="h6">Excel Document Ready</Typography>
            <Typography variant="body2" color="textSecondary">{fileName}</Typography>
            <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
              Preview conversion failed. Please download the document to view it.
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download Document
            </Button>
          </Box>
        );
      }
    }

    // Handle PowerPoint documents
    if (fileType === "powerpoint") {
      return (
        <Box sx={{ width: "100%", height: "100%", overflow: "auto", bgcolor: "white" }}>
          <div dangerouslySetInnerHTML={{ __html: wordHtml }} />
          <Box display="flex" justifyContent="center" p={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ bgcolor: "#D04423", "&:hover": { bgcolor: "#B03820" } }}
            >
              Download Presentation
            </Button>
          </Box>
        </Box>
      );
    }

    // Handle other file types
    switch (fileType) {
      case "pdf":
      case "image":
      case "text":
        return (
          <iframe
            src={blobUrl}
            style={{ width: "100%", height: "100%", border: "none" }}
            title={`${fileType.toUpperCase()} Viewer`}
          />
        );

      default:
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
            <Typography variant="h6">Preview Not Available</Typography>
            <Typography variant="body2" color="textSecondary">
              This file type cannot be previewed directly in the browser.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download File
            </Button>
          </Box>
        );
    }
  };

  return (
    <Box display="flex" flexDirection="column">
      <Fab
        color="secondary"
        aria-label="go back"
        onClick={handleGoBack}
        sx={{
          position: "fixed",
          top: 150,
          left: 285,
          zIndex: 1100,
          width: 40,
          height: 40,
          boxShadow: 3,
          "&:hover": { boxShadow: 6 },
        }}
      >
        <ArrowBackIcon />
      </Fab>

      <DynamicTable
        title="Document Uploads"
        subtitle="Manage all documents related to the case"
        columns={DocumentUploadColumns}
        FormComponent={DocumentUploadForm}
        base_url={base_url}
        actions={actions}
      />

      <Dialog
        open={openDialog}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh", maxHeight: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${colors.primary[300]}`,
            py: 1.5,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">Document Viewer</Typography>
              {fileType && (
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  ({fileType.toUpperCase()})
                </Typography>
              )}
            </Box>
            {fileName && (
              <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                {fileName}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            <IconButton 
              size="small" 
              onClick={handleOpenInNewTab} 
              disabled={!blobUrl}
              title="Open in new tab"
            >
              <OpenInNewIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleDownload} 
              disabled={!blobUrl}
              title="Download"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: "100%" }}>
          {renderDocumentViewer()}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default DocumentUploads;